import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, ICON_SIZE, type IconName } from '../editor/chrome/icons';
import { Popover, type Align, type Placement } from './popover';
import { KeyCaps } from './KeyCaps';
import { tileUrl } from '../editor/content/tileUrl';
import { usePhone } from './usePhone';
import './menu.css';

/**
 * Shared action menus: aligned marks, quiet shortcut hints and separated action groups.
 * Checked choices retain a dedicated column. The keyboard works the way it does in any Mac
 * menu: arrows move, Return chooses, the right arrow opens a submenu and the left one
 * closes it, and typing a letter jumps to the first item that starts with it.
 */

/** Menu rows draw the Core Line marks (Streamline, 2 Nov); a name with no Core Line match keeps its own. */
const MENU_MARK: Partial<Record<IconName, IconName>> = {
  'sidebar-right': 'menu-properties',
  'info': 'menu-info',
  'scissors': 'menu-scissors',
  'copy': 'menu-copy',
  'lock': 'menu-lock',
  'eye-off': 'menu-eye-off',
  'eye': 'menu-eye',
  'export-download': 'menu-export',
  'trash': 'menu-trash',
  'flip-h': 'menu-flip-h',
  'flip-v': 'menu-flip-v',
  'align-left': 'menu-align-left',
  'align-right': 'menu-align-right',
  'align-top': 'menu-align-top',
  'align-bottom': 'menu-align-bottom',
  'align-center': 'menu-align-center',
  'align-middle': 'menu-align-middle',
  'zoom-in': 'menu-zoom-in',
  'zoom-out': 'menu-zoom-out',
  'expand': 'menu-expand',
  'square': 'menu-square',
  'circle': 'menu-circle',
  'star': 'menu-star',
  'text': 'menu-text',
  'text-area': 'menu-text-area',
  'union': 'menu-union',
  'subtract': 'menu-subtract',
  'intersect': 'menu-intersect',
  'exclude': 'menu-exclude',
  'mask': 'menu-mask',
  'bring-forward': 'menu-bring-forward',
  'send-backward': 'menu-send-backward',
  'palette': 'sel-colour',
  'eyedropper': 'sel-dropper',
  'magic': 'sel-effects',
  'generate-sparkles': 'sel-ai',
  'tools-repeat': 'dock-repeat',
  'pictures': 'sel-replace',
  'plus': 'sel-plus',
  'minus': 'sel-minus',
  'selection-crop': 'sel-crop',
  'polygon': 'dock-polygon',
  'pen': 'dock-pen',
  'draw': 'dock-draw',
  'artboard': 'dock-artboard',
  'combine': 'dock-combine',
  'brush': 'dock-brush',
  'knife': 'dock-cut',
  'selection-opacity': 'sel-opacity',
  'opacity': 'sel-opacity',
  'layers': 'sel-arrange',
  'text-align-left': 'sel-align-left', 'text-align-center': 'sel-align-center', 'text-align-right': 'sel-align-right', 'selection-text-align-justify': 'sel-align-justify',
};
const menuMark = (name: IconName): IconName => MENU_MARK[name] ?? name;

export type MenuEntry =
  | {
      readonly kind?: 'item';
      readonly id: string;
      readonly label: string;
      readonly icon?: IconName;
      readonly shortcut?: string;
      readonly checked?: boolean;
      readonly disabled?: boolean;
      readonly danger?: boolean;
      /** Shown under the label, smaller: what the item does, when the label cannot say. */
      readonly note?: string;
      /**
       * A small picture before the label (a version's first artboard): its link, or null while
       * it is still being drawn, which shows the empty frame in its place so the row never moves.
       */
      readonly thumb?: string | null;
      /** An app's App Directory logo before the label, or null for the empty box of the same size (not known yet, or it has none). */
      readonly logo?: string | null;
      /** Run inside the press itself, then close: what it does opens a tab, which a browser allows only there. */
      readonly inPress?: boolean;
      /**
       * Chosen without closing the menu: a setting the rest of the menu depends on, like
       * which artboards an export covers, which would be pointless to pick and then have
       * to open the menu again to use.
       */
      readonly stay?: boolean;
      /** The tool guide a long hover shows beside the row (editor/guides), for a row that picks a tool. */
      readonly guide?: string;
      onSelect(): void;
    }
  | { readonly kind: 'separator'; readonly id?: string }
  | { readonly kind: 'header'; readonly id?: string; readonly label: string }
  | {
      readonly kind: 'submenu';
      readonly id: string;
      readonly label: string;
      readonly icon?: IconName;
      readonly disabled?: boolean;
      readonly items: readonly MenuEntry[];
      /** Its rows from a hook, in place of `items`: run only while it is open, for rows that cost a read to know. */
      readonly useItems?: () => readonly MenuEntry[];
    };

type Actionable = Extract<MenuEntry, { id: string; label: string }> & { kind?: 'item' | 'submenu' };

export function Menu({
  open,
  anchor,
  onClose,
  items,
  placement = 'below',
  align = 'start',
  width,
  label,
  variant = 'standard',
  current,
}: {
  open: boolean;
  anchor: HTMLElement | null;
  onClose: () => void;
  items: readonly MenuEntry[];
  placement?: Placement;
  align?: Align;
  width?: number;
  label?: string;
  /** Where the menu is used; every variant shares the same row and icon sizing. */
  variant?: MenuVariant;
  /** The item the highlight starts on: a pop-up button opens on the choice it holds. */
  current?: string;
}) {
  return (
    <Popover open={open} anchor={anchor} onClose={onClose} placement={placement} align={align} offset={6} width={variant === 'context' ? width ?? 232 : undefined} label={label} role="menu" swallowDismissal={variant === 'context'} className={`menu-surface ${surfaceClass(variant)}`}>
      <MenuList variant={variant} items={items} onClose={onClose} minWidth={width ?? 232} current={current} />
    </Popover>
  );
}

type MenuVariant = 'standard' | 'context' | 'screen';

const surfaceClass = (variant: MenuVariant) => (variant === 'context' ? 'canvas-context-menu' : variant === 'screen' ? 'screen-menu' : '');

type MenuListProps = { variant?: MenuVariant; items: readonly MenuEntry[]; onClose: () => void; minWidth: number; onBack?: () => void; current?: string };

/** A submenu's rows from its hook. */
function HookedMenuList({ useItems, ...rest }: Omit<MenuListProps, 'items'> & { useItems: () => readonly MenuEntry[] }) {
  return <MenuList {...rest} items={useItems()} />;
}

/** An opened submenu's list: its `items`, or what its hook answers. */
function SubmenuList({ entry, ...rest }: Omit<MenuListProps, 'items'> & { entry: Extract<MenuEntry, { kind: 'submenu' }> }) {
  return entry.useItems ? <HookedMenuList {...rest} useItems={entry.useItems} /> : <MenuList {...rest} items={entry.items} />;
}

function MenuList({ items, onClose, minWidth, onBack, variant = 'standard', current }: MenuListProps) {
  const list = useRef<HTMLDivElement>(null);
  const actionable = useMemo(
    () => items.map((item, index) => ({ item, index })).filter(({ item }) => (item.kind === undefined || item.kind === 'item' || item.kind === 'submenu') && !(item as Actionable).disabled),
    [items],
  );
  const [active, setActive] = useState<number | null>(() => {
    const at = current === undefined ? -1 : items.findIndex((item) => 'id' in item && item.id === current && item.kind !== 'separator' && item.kind !== 'header');
    return at < 0 ? null : at;
  });
  const [submenu, setSubmenu] = useState<{ index: number; element: HTMLElement } | null>(null);
  /** On a phone the menu is a sheet, and a submenu opens in its place with a way back, not beside it. */
  const phone = usePhone();
  const hasCheck = items.some((item) => (item.kind === undefined || item.kind === 'item') && item.checked !== undefined);
  // One leading column, for marks only, and only when every row has one: a menu where some
  // rows carry a mark and the rest sit indented under an empty gutter reads as broken (2 Nov).
  const rows = items.filter((item) => item.kind === undefined || item.kind === 'item' || item.kind === 'submenu') as Actionable[];
  const hasIcon = rows.length > 0 && rows.every((item) => !!item.icon);
  const closeSubmenu = () => {
    setSubmenu(null);
    list.current?.focus({ preventScroll: true });
  };

  // The list takes the keyboard as soon as it opens, so arrows work without a click.
  useEffect(() => {
    list.current?.focus({ preventScroll: true });
  }, []);

  const choose = (index: number) => {
    const item = items[index];
    if (!item) return;
    if (item.kind === 'submenu') {
      const element = list.current?.querySelector<HTMLElement>(`[data-index="${index}"]`);
      if (element) setSubmenu({ index, element });
      return;
    }
    if ((item.kind === undefined || item.kind === 'item') && !item.disabled && item.stay) {
      item.onSelect();
      return;
    }
    if ((item.kind === undefined || item.kind === 'item') && !item.disabled && item.inPress) {
      item.onSelect();
      onClose();
      return;
    }
    if ((item.kind === undefined || item.kind === 'item') && !item.disabled) {
      onClose();
      // After the menu has gone, so whatever it opens next is not dismissed by it.
      requestAnimationFrame(() => item.onSelect());
    }
  };

  const drilled = drilledSubmenu(phone, submenu?.index ?? null, items);
  if (drilled) {
    return (
      <DrilledMenu label={drilled.label} onBack={closeSubmenu}>
        <SubmenuList
          variant={variant}
          entry={drilled}
          onClose={() => {
            setSubmenu(null);
            onClose();
          }}
          onBack={closeSubmenu}
          minWidth={0}
        />
      </DrilledMenu>
    );
  }

  const step = (by: number) => {
    if (actionable.length === 0) return;
    const at = actionable.findIndex(({ index }) => index === active);
    const next = at < 0 ? (by > 0 ? 0 : actionable.length - 1) : (at + by + actionable.length) % actionable.length;
    setActive(actionable[next]!.index);
  };

  return (
    <div
      ref={list}
      tabIndex={-1}
      className="menu-list outline-none"
      style={{ minWidth: variant === 'context' ? 0 : minWidth }}
      onKeyDown={(event) => {
        // Portal children still bubble through React: a submenu owns its arrows.
        event.stopPropagation();
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          step(1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          step(-1);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          // The menu's Return is its own: the menu has closed by the time the window hears
          // it, and the Artboard tool took it as Add — one more board, at the old size
          // (QA 2026-09-24, artboards-F4).
          event.stopPropagation();
          if (active !== null) choose(active);
        } else if (event.key === 'ArrowRight') {
          if (active !== null && items[active]?.kind === 'submenu') {
            event.preventDefault();
            choose(active);
          }
        } else if (event.key === 'ArrowLeft' && onBack) {
          event.preventDefault();
          onBack();
        } else if (event.key.length === 1 && /\S/.test(event.key)) {
          const letter = event.key.toLowerCase();
          const found = actionable.find(({ item }) => (item as Actionable).label.toLowerCase().startsWith(letter));
          if (found) setActive(found.index);
        }
      }}
      onPointerLeave={() => {
        if (!submenu) setActive(null);
      }}
    >
      {items.map((item, index) => {
        if (item.kind === 'separator') return <div key={`sep-${index}`} role="separator" className="menu-separator" />;
        if (item.kind === 'header')
          return (
            <div key={`h-${index}`} className="menu-heading" title={item.label}>
              {item.label}
            </div>
          );
        const entry = item as Actionable;
        const lit = active === index;
        const disabled = !!entry.disabled;
        const isSub = entry.kind === 'submenu';
        const plain = entry.kind === undefined || entry.kind === 'item' ? (entry as Extract<MenuEntry, { onSelect(): void }>) : null;
        return (
          <div
            key={entry.id}
            role={plain?.checked !== undefined ? 'menuitemcheckbox' : 'menuitem'}
            data-index={index}
            data-guide={plain?.guide}
            data-active={lit || undefined}
            data-danger={plain?.danger || undefined}
            aria-disabled={disabled || undefined}
            aria-haspopup={isSub ? 'menu' : undefined}
            aria-expanded={isSub ? submenu?.index === index : undefined}
            aria-checked={plain?.checked}
            className="menu-item"
            onPointerEnter={(event) => {
              if (disabled) return;
              setActive(index);
              // A finger's press arrives as an enter first: on a phone only the press drills in.
              if (phone) return;
              if (isSub) setSubmenu({ index, element: event.currentTarget });
              else setSubmenu(null);
            }}
            onClick={() => !disabled && choose(index)}
          >
            {hasIcon && <span className="menu-icon">{entry.icon ? <Icon name={menuMark(entry.icon)} size={16} /> : null}</span>}
            {plain && plain.thumb !== undefined ? (
              <span className="grid h-[28px] w-[40px] shrink-0 place-items-center overflow-hidden rounded-[4px] bg-fill-1 shadow-[inset_0_0_0_1px_var(--separator)]">
                {plain.thumb ? <img src={tileUrl(plain.thumb)} alt="" className="size-full object-cover" draggable={false} /> : null}
              </span>
            ) : null}
            {plain && plain.logo !== undefined ? (
              <span className="menu-icon">{plain.logo ? <img src={plain.logo} alt="" width={16} height={16} draggable={false} /> : <span className="export-app-box" aria-hidden="true" />}</span>
            ) : null}
            <span className="min-w-0 flex-1">
              {/* 18 px, not the row's 16: a truncated line clips what hangs below it, and Segoe
                  UI's descenders reach past 16 at 13 px — the g of Sign Out was cut (ART-ACC-04). */}
              <span className="block truncate leading-[18px]">{entry.label}</span>
              {plain?.note ? <span className="menu-note">{plain.note}</span> : null}
            </span>
            {plain?.shortcut ? <KeyCaps shortcut={plain.shortcut} className="menu-shortcut" /> : null}
            {/* The tick trails the row, so a chosen row keeps its mark and every label starts on one edge. */}
            {hasCheck && <span className="menu-check menu-check-end">{plain?.checked ? <Icon name="check" size={12} /> : null}</span>}
            {isSub ? <span className="menu-chevron"><Icon name="chevron-right" size={ICON_SIZE.indicator} /></span> : null}
          </div>
        );
      })}
      {submenu && items[submenu.index]?.kind === 'submenu' ? (
        <Popover
          open
          anchor={submenu.element}
          onClose={closeSubmenu}
          placement="right"
          align="start"
          offset={4}
          width={variant === 'context' ? 232 : undefined}
          role="menu"
          swallowDismissal={variant === 'context'}
          label={(items[submenu.index] as Extract<MenuEntry, { kind: 'submenu' }>).label}
          className={`menu-surface ${surfaceClass(variant)}`}
        >
          <SubmenuList
            key={(items[submenu.index] as Extract<MenuEntry, { kind: 'submenu' }>).id}
            variant={variant}
            entry={items[submenu.index] as Extract<MenuEntry, { kind: 'submenu' }>}
            onClose={() => {
              setSubmenu(null);
              onClose();
            }}
            onBack={closeSubmenu}
            minWidth={232}
          />
        </Popover>
      ) : null}
    </div>
  );
}

/** The submenu a phone's menu shows in its own place, when one has been pressed open. */
export function drilledSubmenu(phone: boolean, open: number | null, items: readonly MenuEntry[]): Extract<MenuEntry, { kind: 'submenu' }> | null {
  const item = open === null ? undefined : items[open];
  return phone && item?.kind === 'submenu' ? item : null;
}

/** A submenu opened in place on a phone: a way back, named after it, over its rows. */
export function DrilledMenu({ label, onBack, children }: { label: string; onBack: () => void; children: ReactNode }) {
  return (
    <div className="menu-list menu-drilled outline-none">
      <button type="button" className="menu-item menu-back" onClick={onBack}>
        <span className="menu-icon"><Icon name="back" size={16} /></span>
        <span className="min-w-0 flex-1 truncate leading-[18px]">{label}</span>
      </button>
      <div role="separator" className="menu-separator" />
      {children}
    </div>
  );
}

/** A control that opens a menu under itself. Everything else about it is up to the caller. */
export function useMenu() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((was) => !was), []);
  const close = useCallback(() => setOpen(false), []);
  const openMenu = useCallback(() => setOpen(true), []);
  return { anchor, open, ref: setAnchor, toggle, close, openMenu };
}

/** A small caption above a group of controls in a popover or panel. */
export function Caption({ children }: { children: ReactNode }) {
  return <div className="pb-[6px] text-[11px] font-semibold leading-[14px] text-label-2">{children}</div>;
}
